import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { 
  Users as UsersIcon, 
  Plus, 
  Search, 
  Filter,
  Download,
  Upload,
  MoreHorizontal,
  Shield,
  Lock,
  Unlock,
  Activity,
  Settings,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertCircle,
  Edit,
  Trash2,
  Eye,
  Mail,
  Phone,
  Calendar
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuLabel, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/hooks/use-auth";
import { userManagementService, UserManagementUser, UserSearchFilters, UserStats, UsersResponse } from "@/services/userManagementService";

// Use the API-compatible interface
interface User extends UserManagementUser {}

// User activity interface
interface UserActivity {
  id: string;
  userId: string;
  actionType: string;
  actionDetails: any;
  ipAddress: string;
  userAgent: string;
  createdAt: string;
}

export default function UserManagement() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuth();
  
  // State management
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [roleId, setRoleId] = useState<string>('');
  const [status, setStatus] = useState<string>('');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [editFormData, setEditFormData] = useState<Partial<User>>({});
  const [createFormData, setCreateFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    role: 'customer',
    language: 'ar'
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);

  // Build filters object for API
  const filters: UserSearchFilters = useMemo(() => {
    const filterObj: UserSearchFilters = {
      page: currentPage,
      limit: limit
    };
    if (search) filterObj.search = search;
    if (roleId) filterObj.roleId = roleId;
    if (status) filterObj.status = status;
    return filterObj;
  }, [currentPage, limit, search, roleId, status]);

  // React Query for users data
  const { data: usersResponse, isLoading: usersLoading, error: usersError, refetch: refetchUsers } = useQuery({
    queryKey: ['users', filters],
    queryFn: () => userManagementService.getUsers(filters),
    staleTime: 30000, // 30 seconds
  });

  // Extract users and pagination from response
  const users = useMemo(() => {
    return usersResponse?.success && usersResponse?.data ? usersResponse.data.users : [];
  }, [usersResponse]);

  const pagination = useMemo(() => {
    return usersResponse?.success && usersResponse?.data ? usersResponse.data.pagination : {
      total: 0,
      totalPages: 0,
      currentPage: 1,
      perPage: 10
    };
  }, [usersResponse]);

  // React Query for user statistics
  const { data: statsResponse, isLoading: statsLoading } = useQuery({
    queryKey: ['userStats'],
    queryFn: () => userManagementService.getUserStats(),
    staleTime: 60000, // 1 minute
  });

  // Mutations for user operations
  const updateUserStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'active' | 'suspended' }) => {
      // API accepts 'active' or 'inactive', but UI uses 'suspended' - map it
      const apiStatus = status === 'suspended' ? 'inactive' : 'active';
      return userManagementService.updateUserStatus(id, apiStatus);
    },
    onSuccess: () => {
      // Invalidate and refetch users query with exact filters
      queryClient.invalidateQueries({ queryKey: ['users', filters] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['userStats'] });
      refetchUsers(); // Force immediate refetch
      toast({
        title: t("success") || "Success",
        description: t("userStatusUpdated") || "User status updated successfully",
      });
    },
    onError: (error) => {
      toast({
        title: t("error"),
        description: t("failedToUpdateUserStatus"),
        variant: "destructive",
      });
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: (id: string) => userManagementService.deleteUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['userStats'] });
      toast({
        title: t("success"),
        description: t("userDeleted") || "User deleted successfully",
      });
    },
    onError: (error) => {
      toast({
        title: t("error"),
        description: t("failedToDeleteUser") || "Failed to delete user",
        variant: "destructive",
      });
    },
  });

  const editUserMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<User> }) => 
      userManagementService.updateUser(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['userStats'] });
      setIsEditDialogOpen(false);
      setEditFormData({});
      toast({
        title: t("success"),
        description: t("userUpdated") || "User updated successfully",
      });
    },
    onError: (error) => {
      toast({
        title: t("error"),
        description: t("failedToUpdateUser") || "Failed to update user",
        variant: "destructive",
      });
    },
  });

  const createUserMutation = useMutation({
    mutationFn: (userData: any) => userManagementService.createUser(userData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['userStats'] });
      setIsCreateDialogOpen(false);
      setCreateFormData({
        name: '',
        email: '',
        phone: '',
        password: '',
        role: 'customer',
        language: 'ar'
      });
      toast({
        title: t("success"),
        description: t("userCreated") || "User created successfully",
      });
    },
    onError: (error) => {
      toast({
        title: t("error"),
        description: t("failedToCreateUser") || "Failed to create user",
        variant: "destructive",
      });
    },
  });

  const bulkUpdateMutation = useMutation({
    mutationFn: ({ userIds, action, data }: { userIds: string[]; action: string; data?: any }) =>
      userManagementService.bulkUpdateUsers(userIds, action, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['userStats'] });
      setSelectedUsers([]);
      toast({
        title: t("success"),
        description: t("bulkOperationCompleted"),
      });
    },
    onError: (error) => {
      toast({
        title: t("error"),
        description: t("bulkOperationFailed"),
        variant: "destructive",
      });
    },
  });

  // Extract user stats (can be calculated from users or separate endpoint)
  const userStats = useMemo(() => {
    if (users.length > 0) {
      return {
        total: pagination.total,
        active: users.filter(u => u.status === 'active').length,
        blocked: users.filter(u => u.status === 'suspended' || u.status === 'deleted').length,
        admins: users.filter(u => u.roleId === 2).length,
        staff: users.filter(u => u.roleId === 3).length,
        customers: users.filter(u => u.roleId === 1).length
      };
    }
    return { total: 0, active: 0, blocked: 0, admins: 0, staff: 0, customers: 0 };
  }, [users, pagination]);

  // No need for client-side filtering since API handles it
  const isLoading = usersLoading || statsLoading;

  // Role display helpers
  const getRoleDisplay = (role: string) => {
    const roleMap = {
      'super_admin': { label: t('superAdmin') || 'Super Admin', color: 'bg-red-500' },
      'admin': { label: t('admin') || 'Admin', color: 'bg-blue-500' },
      'staff': { label: t('staff') || 'Staff', color: 'bg-green-500' },
      'customer': { label: t('customer') || 'Customer', color: 'bg-gray-500' }
    };
    return roleMap[role as keyof typeof roleMap] || { label: role, color: 'bg-gray-500' };
  };

  const getStatusDisplay = (status: string) => {
    const statusMap = {
      'active': { label: t('active') || 'Active', color: 'bg-green-500', icon: CheckCircle },
      'suspended': { label: t('suspended') || 'Suspended', color: 'bg-yellow-500', icon: AlertCircle },
      'deleted': { label: t('deleted') || 'Deleted', color: 'bg-red-500', icon: XCircle },
     };
    return statusMap[status as keyof typeof statusMap] || { label: status, color: 'bg-gray-500', icon: AlertCircle };
  };

  // Event handlers
  const handleSelectUser = (userId: string, checked: boolean) => {
    if (checked) {
      setSelectedUsers([...selectedUsers, userId]);
    } else {
      setSelectedUsers(selectedUsers.filter(id => id !== userId));
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedUsers(users.map(user => user.id));
    } else {
      setSelectedUsers([]);
    }
  };

  const handleViewUser = (user: User) => {
    setSelectedUser(user);
    setIsViewDialogOpen(true);
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setEditFormData({
      name: user.name,
      email: user.email,
      phone: user.phone,
      language: user.language,
      role: user.role
    });
    setIsEditDialogOpen(true);
  };

  const handleSaveEdit = () => {
    if (!selectedUser) return;
    
    // Only send the fields that can be updated
    const updateData: any = {};
    if (editFormData.name && editFormData.name !== selectedUser.name) {
      updateData.name = editFormData.name;
    }
    if (editFormData.email && editFormData.email !== selectedUser.email) {
      updateData.email = editFormData.email;
    }
    if (editFormData.phone && editFormData.phone !== selectedUser.phone) {
      updateData.phone = editFormData.phone;
    }
    if (editFormData.language && editFormData.language !== selectedUser.language) {
      updateData.language = editFormData.language;
    }
    if (editFormData.role && editFormData.role !== selectedUser.role) {
      updateData.role = editFormData.role;
    }

    if (Object.keys(updateData).length === 0) {
      toast({
        title: t("noChanges") || "No Changes",
        description: t("noChangesToSave") || "No changes to save",
        variant: "default",
      });
      return;
    }

    editUserMutation.mutate({ id: selectedUser.id, data: updateData });
  };

  const handleCreateUser = () => {
    // Validate required fields
    if (!createFormData.name || !createFormData.email || !createFormData.phone || !createFormData.password) {
      toast({
        title: t("validationError") || "Validation Error",
        description: t("pleaseCompleteAllFields") || "Please complete all required fields",
        variant: "destructive",
      });
      return;
    }

    createUserMutation.mutate(createFormData);
  };

  const handleUserStatusChange = (userId: string, newStatus: 'active' | 'suspended') => {
    updateUserStatusMutation.mutate({ id: userId, status: newStatus });
  };

  const handleBulkAction = (action: string) => {
    if (selectedUsers.length === 0) {
      toast({
        title: t("noUsersSelected"),
        description: t("pleaseSelectUsers"),
        variant: "destructive",
      });
      return;
    }

    bulkUpdateMutation.mutate({ userIds: selectedUsers, action });
  };

  const handleDeleteUser = (user: User) => {
    const confirmMessage = `Are you sure you want to delete user "${user.name}"? This action cannot be undone.`;
    if (confirm(confirmMessage)) {
      deleteUserMutation.mutate(user.id);
    }
  };

  // Password reset state
  const [isResetPasswordDialogOpen, setIsResetPasswordDialogOpen] = useState(false);
  const [resetPasswordUser, setResetPasswordUser] = useState<User | null>(null);
  const [resetPasswordStep, setResetPasswordStep] = useState<'request' | 'reset'>('request');
  const [resetPasswordOTP, setResetPasswordOTP] = useState('');
  const [resetPasswordData, setResetPasswordData] = useState({
    newPassword: '',
    confirmPassword: ''
  });

  const handleRequestPasswordReset = (user: User) => {
    setResetPasswordUser(user);
    setIsResetPasswordDialogOpen(true);
    setResetPasswordStep('request');
    setResetPasswordOTP('');
    setResetPasswordData({ newPassword: '', confirmPassword: '' });
  };

  const handleSendOTP = async () => {
    if (!resetPasswordUser) return;

    try {
      const response = await userManagementService.requestPasswordReset(resetPasswordUser.phone, false);
      if (response.success && response.data) {
        setResetPasswordOTP(response.data.otp);
        toast({
          title: t("success") || "Success",
          description: `${t('otpSentVia') || 'OTP sent via'} ${response.data.sentVia}. ${t('otp') || 'OTP'}: ${response.data.otp} (${t('forTestingPurposes') || 'for testing purposes'})`,
        });
        // Move to reset step after OTP is sent
        setResetPasswordStep('reset');
      }
    } catch (error: any) {
      toast({
        title: t("error") || "Error",
        description: error?.message || t("failedToRequestPasswordReset") || "Failed to request password reset",
        variant: "destructive",
      });
    }
  };

  const handleResetPassword = async () => {
    if (!resetPasswordUser) return;

    // Validation
    if (!resetPasswordOTP) {
      toast({
        title: t("validationError") || "Validation Error",
        description: t("pleaseEnterOTP") || "Please enter OTP",
        variant: "destructive",
      });
      return;
    }

    if (!resetPasswordData.newPassword || !resetPasswordData.confirmPassword) {
      toast({
        title: t("validationError") || "Validation Error",
        description: t("pleaseEnterPasswordAndConfirmation") || "Please enter new password and confirmation",
        variant: "destructive",
      });
      return;
    }

    if (resetPasswordData.newPassword.length < 8) {
      toast({
        title: t("validationError") || "Validation Error",
        description: t("passwordTooShort") || "Password must be at least 8 characters",
        variant: "destructive",
      });
      return;
    }

    if (resetPasswordData.newPassword !== resetPasswordData.confirmPassword) {
      toast({
        title: t("validationError") || "Validation Error",
        description: t("passwordsDoNotMatch") || "Passwords do not match",
        variant: "destructive",
      });
      return;
    }

    try {
      const response = await userManagementService.resetPassword(
        resetPasswordUser.phone,
        resetPasswordOTP,
        resetPasswordData.newPassword,
        resetPasswordData.confirmPassword
      );

      if (response.success) {
        toast({
          title: t("success") || "Success",
          description: response.message || t("passwordResetSuccessful") || "Password reset successful",
        });
        setIsResetPasswordDialogOpen(false);
        setResetPasswordUser(null);
        setResetPasswordOTP('');
        setResetPasswordData({ newPassword: '', confirmPassword: '' });
        setResetPasswordStep('request');
      }
    } catch (error: any) {
      // Display API validation errors if available
      let errorMessage = error?.message || t("failedToResetPassword") || "Failed to reset password";
      
      if (error?.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        const errorMessages = error.response.data.errors.map((err: any) => err.message).join(', ');
        if (errorMessages) {
          errorMessage = errorMessages;
        }
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      toast({
        title: t("error") || "Error",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  const handleExport = async (format: 'csv' | 'excel') => {
    try {
      const response = await userManagementService.exportUsers(filters, format);
      if (response.success && response.data?.downloadUrl) {
        // Open download URL
        window.open(response.data.downloadUrl, '_blank');
        toast({
          title: t("exportStarted"),
          description: t("downloadWillStartShortly"),
        });
      }
    } catch (error) {
      toast({
        title: t("error"),
        description: t("exportFailed"),
        variant: "destructive",
      });
    }
  };

  // Handle filter changes - reset to page 1 when filters change
  const handleSearchChange = (value: string) => {
    setSearch(value);
    setCurrentPage(1);
  };

  const handleRoleChange = (value: string) => {
    setRoleId(value === 'all' ? '' : value);
    setCurrentPage(1);
  };

  const handleStatusChange = (value: string) => {
    setStatus(value === 'all' ? '' : value);
    setCurrentPage(1);
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t("userManagement")}</h1>
          <p className="text-muted-foreground">
            {t("manageUsers")}
          </p>
        </div>
        
        <div className="flex space-x-2">
           
             
          <Button onClick={() => setIsCreateDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            {t("addUser")}
          </Button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("totalUsers")}</CardTitle>
            <UsersIcon className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{isLoading ? "..." : userStats.total}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("activeUsers")}</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{isLoading ? "..." : userStats.active}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("blockedUsers")}</CardTitle>
            <XCircle className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{isLoading ? "..." : userStats.blocked}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("adminUsers")}</CardTitle>
            <Shield className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{isLoading ? "..." : userStats.admins}</div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Filter className="mr-2 h-5 w-5" />
            {t("searchAndFilters")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-2">
              <Label htmlFor="search">{t("search")}</Label>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="search"
                  placeholder={t("searchUsers") || "Search by name, phone, or email"}
                  value={search}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>{t("role")}</Label>
              <Select value={roleId || 'all'} onValueChange={handleRoleChange}>
                <SelectTrigger>
                  <SelectValue placeholder={t("selectRole") || "Select Role"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("allRoles") || "All Roles"}</SelectItem>
                  <SelectItem value="1">{t("customer") || "Customer"}</SelectItem>
                  <SelectItem value="2">{t("admin") || "Admin"}</SelectItem>
                  <SelectItem value="3">{t("staff") || "Staff"}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>{t("status")}</Label>
              <Select value={status || 'all'} onValueChange={handleStatusChange}>
                <SelectTrigger>
                  <SelectValue placeholder={t("selectStatus") || "Select Status"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("allStatuses") || "All Statuses"}</SelectItem>
                  <SelectItem value="active">{t("active") || "Active"}</SelectItem>
                  <SelectItem value="suspended">{t("suspended") || "Suspended"}</SelectItem>
                  <SelectItem value="deleted">{t("deleted") || "Deleted"}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>{t("pageSize") || "Items per page"}</Label>
              <Select value={limit.toString()} onValueChange={(value) => {
                setLimit(parseInt(value));
                setCurrentPage(1);
              }}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bulk Actions */}
      {selectedUsers.length > 0 && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                {selectedUsers.length} {t("usersSelected")}
              </span>
              
              <div className="flex space-x-2">
                <Button variant="outline" size="sm" onClick={() => handleBulkAction('activate')}>
                  <CheckCircle className="mr-2 h-4 w-4" />
                  {t("activate")}
                </Button>
                
                <Button variant="outline" size="sm" onClick={() => handleBulkAction('block')}>
                  <Lock className="mr-2 h-4 w-4" />
                  {t("block")}
                </Button>
                
                <Button variant="outline" size="sm" onClick={() => handleBulkAction('delete')}>
                  <Trash2 className="mr-2 h-4 w-4" />
                  {t("delete")}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Users Table */}
      <Card>
        <CardHeader>
          <CardTitle>{t("users")} ({isLoading ? "..." : pagination.total})</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[50px]">
                  <Checkbox
                    checked={selectedUsers.length === users.length && users.length > 0}
                    onCheckedChange={handleSelectAll}
                  />
                </TableHead>
                <TableHead>{t("user")}</TableHead>
                <TableHead>{t("contact")}</TableHead>
                <TableHead>{t("role")}</TableHead>
                <TableHead>{t("status")}</TableHead>
                <TableHead>{t("lastLogin")}</TableHead>
                <TableHead className="text-right">{t("actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    <div className="flex items-center justify-center">
                      <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                      {t("loading")}...
                    </div>
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    <div className="text-muted-foreground">
                      {t("noUsersFound")}
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => {
                const roleDisplay = getRoleDisplay(user.role || 'customer');
                const statusDisplay = getStatusDisplay(user.status || 'active');
                const StatusIcon = statusDisplay.icon;
                
                return (
                  <TableRow key={user.id}>
                    <TableCell>
                      <Checkbox
                        checked={selectedUsers.includes(user.id)}
                        onCheckedChange={(checked) => handleSelectUser(user.id, checked as boolean)}
                      />
                    </TableCell>
                    
                    <TableCell>
                      <div className="flex items-center space-x-3">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={user.avatar} />
                          <AvatarFallback>{user.name.charAt(0)}</AvatarFallback>
                        </Avatar>
                        <div>
                          <div className="font-medium">{user.name}</div>
                          <div className="text-sm text-muted-foreground">{t("id") || "ID"}: {user.id}</div>
                        </div>
                      </div>
                    </TableCell>
                    
                    <TableCell>
                      <div className="space-y-1">
                        {user.email && (
                          <div className="flex items-center text-sm">
                            <Mail className="mr-2 h-3 w-3" />
                            {user.email}
                          </div>
                        )}
                        <div className="flex items-center text-sm">
                          <Phone className="mr-2 h-3 w-3" />
                          {user.phone}
                        </div>
                      </div>
                    </TableCell>
                    
                    <TableCell>
                      <Badge className={`${roleDisplay.color} text-white`}>
                        {roleDisplay.label}
                      </Badge>
                    </TableCell>
                    
                    <TableCell>
                      <div className="flex items-center">
                        <StatusIcon className="mr-2 h-4 w-4" />
                        <Badge variant={user.status === 'active' ? 'default' : 'secondary'}>
                          {statusDisplay.label}
                        </Badge>
                      </div>
                    </TableCell>
                    
                    <TableCell>
                      <div className="text-sm">
                        {user.last_login_at ? new Date(user.last_login_at).toLocaleString() : t("never") || "Never"}
                      </div>
                    </TableCell>
                    
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        {/* Quick Edit Button */}
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleEditUser(user)}
                          className="h-8 w-8 p-0"
                          title={t("edit") || "Edit User"}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        
                        {/* Quick Delete Button */}
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleDeleteUser(user)}
                          className="h-8 w-8 p-0 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                          title={t("delete") || "Delete User"}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                        
                        {/* More Actions Dropdown */}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="h-8 w-8 p-0">
                              <span className="sr-only">{t("openMenu")}</span>
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuLabel>{t("actions") || "Actions"}</DropdownMenuLabel>
                            <DropdownMenuItem onClick={() => handleViewUser(user)}>
                              <Eye className="mr-2 h-4 w-4" />
                              {t("view") || "View"}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => {
                              // Toggle between 'active' and 'suspended'
                              // If current status is 'active', set to 'suspended', otherwise set to 'active'
                              const newStatus = user.status === 'active' ? 'suspended' : 'active';
                              handleUserStatusChange(user.id, newStatus);
                            }}>
                              {user.status === 'active' ? (
                                <>
                                  <Lock className="mr-2 h-4 w-4" />
                                  {t("deactivate") || "Deactivate"}
                                </>
                              ) : (
                                <>
                                  <Unlock className="mr-2 h-4 w-4" />
                                  {t("activate") || "Activate"}
                                </>
                              )}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleRequestPasswordReset(user)}>
                              <RefreshCw className="mr-2 h-4 w-4" />
                              {t("resetPassword") || "Reset Password"}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                );
                })
              )}
            </TableBody>
          </Table>
          
          {/* Pagination */}
          {pagination.totalPages > 0 && (
            <div className="flex items-center justify-between space-x-2 py-4">
              <div className="text-sm text-muted-foreground">
                {t("showing") || "Showing"} {(pagination.currentPage - 1) * pagination.perPage + 1} - {Math.min(pagination.currentPage * pagination.perPage, pagination.total)} {t("of") || "of"} {pagination.total} {t("users") || "users"}
              </div>
              
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1 || isLoading}
                >
                  {t("previous") || "Previous"}
                </Button>
                
                {/* Page numbers */}
                <div className="flex items-center space-x-1">
                  {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                    let pageNum;
                    if (pagination.totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= pagination.totalPages - 2) {
                      pageNum = pagination.totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <Button
                        key={pageNum}
                        variant={currentPage === pageNum ? "default" : "outline"}
                        size="sm"
                        onClick={() => setCurrentPage(pageNum)}
                        disabled={isLoading}
                        className="w-10"
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(Math.min(pagination.totalPages, currentPage + 1))}
                  disabled={currentPage === pagination.totalPages || isLoading}
                >
                  {t("next") || "Next"}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create User Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t("createUser") || "Create New User"}</DialogTitle>
            <DialogDescription>
              {t("createUserDescription") || "Add a new user to the system"}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="create-name">{t("name") || "Name"} *</Label>
              <Input
                id="create-name"
                value={createFormData.name}
                onChange={(e) => setCreateFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder={t("enterName") || "Enter full name"}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="create-email">{t("email") || "Email"} *</Label>
              <Input
                id="create-email"
                type="email"
                value={createFormData.email}
                onChange={(e) => setCreateFormData(prev => ({ ...prev, email: e.target.value }))}
                placeholder={t("enterEmail") || "Enter email address"}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="create-phone">{t("phone") || "Phone"} *</Label>
              <Input
                id="create-phone"
                value={createFormData.phone}
                onChange={(e) => setCreateFormData(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="+966500000000"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="create-password">{t("password") || "Password"} *</Label>
              <Input
                id="create-password"
                type="password"
                value={createFormData.password}
                onChange={(e) => setCreateFormData(prev => ({ ...prev, password: e.target.value }))}
                placeholder={t("enterPassword") || "Enter password"}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="create-role">{t("role") || "Role"}</Label>
              <Select 
                value={createFormData.role} 
                onValueChange={(value) => setCreateFormData(prev => ({ ...prev, role: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t("selectRole") || "Select role"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="customer">{t("customer") || "Customer"}</SelectItem>
                  <SelectItem value="staff">{t("staff") || "Staff"}</SelectItem>
                  <SelectItem value="admin">{t("admin") || "Admin"}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="create-language">{t("language") || "Language"}</Label>
              <Select 
                value={createFormData.language} 
                onValueChange={(value) => setCreateFormData(prev => ({ ...prev, language: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t("selectLanguage") || "Select language"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="ar">العربية</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
              {t("cancel") || "Cancel"}
            </Button>
            <Button 
              onClick={handleCreateUser}
              disabled={createUserMutation.isPending}
            >
              {createUserMutation.isPending ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  {t("creating") || "Creating..."}
                </>
              ) : (
                <>
                  <Plus className="mr-2 h-4 w-4" />
                  {t("createUser") || "Create User"}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t("editUser") || "Edit User"}</DialogTitle>
            <DialogDescription>
              {t("updateUserInformation") || "Update user information"}
            </DialogDescription>
          </DialogHeader>
          
          {selectedUser && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit-name">{t("name") || "Name"}</Label>
                <Input
                  id="edit-name"
                  value={editFormData.name || ''}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder={t("enterName") || "Enter name"}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit-email">{t("email") || "Email"}</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={editFormData.email || ''}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder={t("enterEmail") || "Enter email"}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit-phone">{t("phone") || "Phone"}</Label>
                <Input
                  id="edit-phone"
                  value={editFormData.phone || ''}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder={t("enterPhone") || "Enter phone"}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit-role">{t("role") || "Role"}</Label>
                <Select 
                  value={editFormData.role || ''} 
                  onValueChange={(value) => setEditFormData(prev => ({ ...prev, role: value as any }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t("selectRole") || "Select role"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="customer">{t("customer") || "Customer"}</SelectItem>
                    <SelectItem value="staff">{t("staff") || "Staff"}</SelectItem>
                    <SelectItem value="admin">{t("admin") || "Admin"}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit-language">{t("language") || "Language"}</Label>
                <Select 
                  value={editFormData.language || ''} 
                  onValueChange={(value) => setEditFormData(prev => ({ ...prev, language: value as any }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t("selectLanguage") || "Select language"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="ar">العربية</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              {t("cancel") || "Cancel"}
            </Button>
            <Button 
              onClick={handleSaveEdit}
              disabled={editUserMutation.isPending}
            >
              {editUserMutation.isPending ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  {t("saving") || "Saving..."}
                </>
              ) : (
                <>
                  <Edit className="mr-2 h-4 w-4" />
                  {t("saveChanges") || "Save Changes"}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Details Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t("userDetails")}</DialogTitle>
            <DialogDescription>
              {t("viewUserInformation")}
            </DialogDescription>
          </DialogHeader>
          
          {selectedUser && (
            <div className="space-y-4">
              <div className="flex items-center space-x-4">
                <Avatar className="h-16 w-16">
                  <AvatarImage src={selectedUser.avatar} />
                  <AvatarFallback className="text-lg">{selectedUser.name.charAt(0)}</AvatarFallback>
                </Avatar>
                <div>
                  <h3 className="text-lg font-semibold">{selectedUser.name}</h3>
                  <p className="text-muted-foreground">{selectedUser.email}</p>
                </div>
              </div>
              
              <Tabs defaultValue="profile" className="w-full">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="profile">{t("profile")}</TabsTrigger>
                  <TabsTrigger value="activity">{t("activity")}</TabsTrigger>
                  <TabsTrigger value="settings">{t("settings")}</TabsTrigger>
                </TabsList>
                
                <TabsContent value="profile" className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>{t("phone")}</Label>
                      <p className="text-sm">{selectedUser.phone}</p>
                    </div>
                    <div>
                      <Label>{t("role")}</Label>
                      <p className="text-sm">{getRoleDisplay(selectedUser.role || 'customer').label}</p>
                    </div>
                    <div>
                      <Label>{t("status")}</Label>
                      <p className="text-sm">{getStatusDisplay(selectedUser.status || 'active').label}</p>
                    </div>
                    <div>
                      <Label>{t("language")}</Label>
                      <p className="text-sm">{selectedUser.language}</p>
                    </div>
                    <div>
                      <Label>{t("registrationDate")}</Label>
                      <p className="text-sm">{selectedUser.registrationDate ? new Date(selectedUser.registrationDate).toLocaleDateString() : t("unknown") || "Unknown"}</p>
                    </div>
                    <div>
                      <Label>{t("lastLogin")}</Label>
                      <p className="text-sm">{selectedUser.lastLogin ? new Date(selectedUser.lastLogin).toLocaleDateString() : t("never") || "Never"}</p>
                    </div>
                  </div>
                </TabsContent>
                
                <TabsContent value="activity">
                  <div className="text-center py-4">
                    <Activity className="mx-auto h-8 w-8 text-muted-foreground" />
                    <p className="text-muted-foreground mt-2">{t("activityLogsWillBeShownHere")}</p>
                  </div>
                </TabsContent>
                
                <TabsContent value="settings">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <Label>{t("twoFactorAuthentication")}</Label>
                      <Badge variant={selectedUser.twoFactorEnabled ? "default" : "secondary"}>
                        {selectedUser.twoFactorEnabled ? t("enabled") : t("disabled")}
                      </Badge>
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsViewDialogOpen(false)}>
              {t("close")}
            </Button>
            {selectedUser && (
              <Button onClick={() => handleEditUser(selectedUser)}>
                <Edit className="mr-2 h-4 w-4" />
                {t("edit")}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={isResetPasswordDialogOpen} onOpenChange={setIsResetPasswordDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t("resetPassword") || "Reset Password"}</DialogTitle>
            <DialogDescription>
              {resetPasswordUser && resetPasswordStep === 'request' 
                ? `${t("requestOTPToResetPassword") || "Request OTP to reset password for"} ${resetPasswordUser.name}`
                : `${t("enterOTPAndNewPassword") || "Enter OTP and new password for"} ${resetPasswordUser?.name}`
              }
            </DialogDescription>
          </DialogHeader>
          
          {resetPasswordStep === 'request' && resetPasswordUser && (
            <div className="space-y-4">
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground">
                  {t("otpWillBeSent") || "An OTP will be sent to"} {resetPasswordUser.phone} {t("viaSMS") || "via SMS"}
                </p>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => {
                  setIsResetPasswordDialogOpen(false);
                  setResetPasswordUser(null);
                }}>
                  {t("cancel") || "Cancel"}
                </Button>
                <Button onClick={handleSendOTP}>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  {t("sendOTP") || "Send OTP"}
                </Button>
              </DialogFooter>
            </div>
          )}

          {resetPasswordStep === 'reset' && resetPasswordUser && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="reset-otp">{t("otp") || "OTP"}</Label>
                <Input
                  id="reset-otp"
                  type="text"
                  placeholder={t("enterOTP") || "Enter OTP"}
                  value={resetPasswordOTP}
                  onChange={(e) => setResetPasswordOTP(e.target.value)}
                  maxLength={6}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="reset-new-password">{t("newPassword") || "New Password"}</Label>
                <Input
                  id="reset-new-password"
                  type="password"
                  placeholder={t("enterNewPassword") || "Enter new password (min 8 characters)"}
                  value={resetPasswordData.newPassword}
                  onChange={(e) => setResetPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                  minLength={8}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="reset-confirm-password">{t("confirmPassword") || "Confirm Password"}</Label>
                <Input
                  id="reset-confirm-password"
                  type="password"
                  placeholder={t("confirmNewPassword") || "Confirm new password"}
                  value={resetPasswordData.confirmPassword}
                  onChange={(e) => setResetPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  minLength={8}
                />
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => {
                  setIsResetPasswordDialogOpen(false);
                  setResetPasswordUser(null);
                  setResetPasswordOTP('');
                  setResetPasswordData({ newPassword: '', confirmPassword: '' });
                  setResetPasswordStep('request');
                }}>
                  {t("cancel") || "Cancel"}
                </Button>
                <Button onClick={handleResetPassword}>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  {t("resetPassword") || "Reset Password"}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
